import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Ear, LayoutGrid, Video, TrendingUp } from 'lucide-react';
import AnimatedText from './AnimatedText';

const steps = [
  {
    n: '01',
    title: 'Listen',
    body: "A deep dive into your brand, audience, numbers, and what's worked (and hasn't) so far.",
    Icon: Ear,
  },
  {
    n: '02',
    title: 'Plan',
    body: 'A content strategy tied to real goals — follower quality, engagement, or leads. No vibes, a plan.',
    Icon: LayoutGrid,
  },
  {
    n: '03',
    title: 'Create',
    body: 'Short-form, UGC, and creator content built native to each platform. Shot, edited, captioned, scheduled.',
    Icon: Video,
  },
  {
    n: '04',
    title: 'Scale',
    body: 'Every 30 days we review what worked, double down on winners, cut the rest. Growth compounds.',
    Icon: TrendingUp,
  },
];

const StepRow = ({ step, i, total }) => {
  const rowRef = useRef(null);
  const inView = useInView(rowRef, { once: true, margin: '-100px' });
  const isLast = i === total - 1;

  return (
    <div
      ref={rowRef}
      data-testid={`process-step-${step.n}`}
      className="relative grid grid-cols-[auto_1fr] sm:grid-cols-[140px_1fr] lg:grid-cols-[200px_60px_1fr] gap-6 sm:gap-8 items-start py-14 sm:py-20"
    >
      {/* Giant animated number */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.6, rotate: -10 }}
        animate={inView ? { opacity: 1, y: 0, scale: 1, rotate: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.2, 0.65, 0.3, 0.9] }}
        className="text-6xl sm:text-8xl lg:text-9xl font-black tracking-[-0.05em] leading-none text-[#AA94FF]"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {step.n}
      </motion.div>

      {/* Center timeline column (desktop) — with pulsing dot */}
      <div className="hidden lg:flex flex-col items-center pt-6 relative">
        <motion.div
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="relative w-5 h-5 rounded-full bg-[#AA94FF] z-10"
        >
          {/* Pulsing ping */}
          <motion.span
            animate={{ scale: [1, 2.4, 1], opacity: [0.7, 0, 0.7] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
            className="absolute inset-0 rounded-full bg-[#AA94FF]"
          />
        </motion.div>
      </div>

      {/* Text column */}
      <div className="max-w-2xl">
        {/* Icon + title row */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex items-center gap-4 mb-5"
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F4F4F3]/10 border border-[#F4F4F3]/15 flex items-center justify-center">
            <step.Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#AA94FF]" strokeWidth={2} />
          </div>
          <AnimatedText
            as="h3"
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.02em] text-[#F4F4F3]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            y={30}
            stagger={0.04}
          >
            {step.title}
          </AnimatedText>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.35 }}
          className="text-base sm:text-lg lg:text-xl text-[#F4F4F3]/70 leading-relaxed"
        >
          {step.body}
        </motion.p>

        {/* Underline divider */}
        {!isLast && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.9, delay: 0.5, ease: 'easeOut' }}
            className="mt-14 h-[1px] bg-gradient-to-r from-[#F4F4F3]/25 via-[#AA94FF]/40 to-transparent origin-left"
          />
        )}
      </div>
    </div>
  );
};

const Process = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  // Vertical timeline line grows as you scroll through the section
  const lineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);
  // Big background wordmark drifts horizontally
  const wordmarkX = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  // Rotating radial glow
  const glowRotate = useTransform(scrollYProgress, [0, 1], [0, 360]);

  return (
    <section
      ref={sectionRef}
      id="process"
      data-testid="process-section"
      className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#1C1917] text-[#F4F4F3] overflow-hidden"
    >
      {/* Rotating orange glow in background */}
      <motion.div
        aria-hidden="true"
        style={{ rotate: glowRotate }}
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px]"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-[#AA94FF]/8 blur-3xl" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 sm:mb-24">
          <p className="lg:col-span-4 text-xs font-bold uppercase tracking-[0.25em] text-[#AA94FF]">
            Our process
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={50}
              stagger={0.05}
            >
              From strategy to scroll-stopping content.
            </AnimatedText>
          </div>
        </div>

        {/* Timeline + steps */}
        <div className="relative">
          {/* Vertical scroll-driven progress line (desktop only) */}
          <motion.div
            aria-hidden="true"
            style={{ scaleY: lineScale }}
            className="hidden lg:block absolute left-[230px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-[#AA94FF] via-[#AA94FF]/60 to-[#AA94FF]/0 origin-top"
          />

          {steps.map((s, i) => (
            <StepRow key={s.n} step={s} i={i} total={steps.length} />
          ))}
        </div>

        {/* Decorative parallax wordmark */}
        <motion.p
          aria-hidden="true"
          style={{ x: wordmarkX, fontFamily: "'Cabinet Grotesk', sans-serif" }}
          className="mt-16 sm:mt-24 text-[18vw] lg:text-[12rem] font-black tracking-[-0.06em] leading-none text-[#F4F4F3]/[0.04] whitespace-nowrap select-none pointer-events-none"
        >
          LISTEN · PLAN · CREATE · SCALE
        </motion.p>
      </div>
    </section>
  );
};

export default Process;
